// Instagram 데이터 변환 공통 유틸리티

export interface UnifiedInstagramAccount {
    id: string;
    user_id: string;
    instagram_id: string;
    username: string;
    full_name: string;
    biography: string;
    profile_picture_url: string;
    profile_picture_hd_url?: string;
    followers_count: number;
    following_count: number;
    posts_count: number;
    is_private: boolean;
    is_verified: boolean;
    external_url: string;
    created_at: string;
    updated_at: string;
    last_synced_at: string;
    latest_posts?: any[];
    // 최적화 관련
    is_imagekit_optimized?: boolean;
    original_profile_pic_url?: string;
}

/**
 * saved_instagram_accounts 테이블 데이터를 UnifiedInstagramAccount로 변환
 */
export const transformSavedAccountToUnified = (savedAccount: any): UnifiedInstagramAccount => {
    if (!savedAccount) {
        throw new Error('저장된 계정 데이터가 없습니다.');
    }

    const profileData = savedAccount.profile_data || {};

    // ImageKit 최적화 이미지 우선 사용, 원본 fallback
    const profilePicUrl = savedAccount.supabase_profile_pic_url ||
        profileData.profilePicUrl ||
        '';

    const profilePicHdUrl = savedAccount.supabase_profile_hd_pic_url ||
        profileData.profilePicUrlHD ||
        profilePicUrl;

    // External URL 처리 - 배열이면 첫 번째, 문자열이면 그대로
    let externalUrl = '';
    if (profileData.externalUrl) {
        externalUrl = profileData.externalUrl;
    } else if (profileData.externalUrls && Array.isArray(profileData.externalUrls) && profileData.externalUrls.length > 0) {
        externalUrl = profileData.externalUrls[0];
    }

    // 게시물 데이터 ImageKit URL 매핑 처리
    const processedPosts = (profileData.latestPosts || []).map((post: any) => ({
        ...post,
        // ImageKit URL 우선, 원본 URL fallback
        imagekit_url: post.imagekitUrl || null,
        display_url: post.imagekitUrl || post.displayUrl || post.display_url,
        original_image_url: post.originalDisplayUrl || post.displayUrl || post.display_url,
        is_imagekit_optimized: !!post.imagekitUrl,
        // 추가 필드 매핑
        instagram_post_id: post.id,
        shortcode: post.shortCode,
        caption: post.caption,
        likes_count: post.likesCount,
        comments_count: post.commentsCount,
        post_type: post.type,
        timestamp: post.timestamp,
        video_url: post.videoUrl,
        video_view_count: post.videoViewCount
    }));

    console.log(`🖼️ [Transform] 게시물 ImageKit 매핑: ${processedPosts.length}개 중 ${processedPosts.filter((p: any) => p.is_imagekit_optimized).length}개 최적화됨`);

    return {
        id: savedAccount.id,
        user_id: savedAccount.user_id,
        instagram_id: profileData.id || `${savedAccount.username}_${Date.now()}`,
        username: savedAccount.username,
        full_name: profileData.fullName || '',
        biography: profileData.biography || '',
        profile_picture_url: profilePicUrl,
        profile_picture_hd_url: profilePicHdUrl,
        followers_count: profileData.followersCount || 0,
        following_count: profileData.followsCount || profileData.followingCount || 0,
        posts_count: profileData.postsCount || 0,
        is_private: profileData.private || false,
        is_verified: profileData.verified || false,
        external_url: externalUrl,
        created_at: savedAccount.created_at || new Date().toISOString(),
        updated_at: savedAccount.updated_at || new Date().toISOString(),
        last_synced_at: savedAccount.last_data_update || savedAccount.updated_at || new Date().toISOString(),
        latest_posts: processedPosts,
        // 최적화 정보
        is_imagekit_optimized: !!savedAccount.supabase_profile_pic_url,
        original_profile_pic_url: profileData.profilePicUrl
    };
};

/**
 * UnifiedInstagramAccount를 InstagramProfile (Store용)로 변환
 */
export const transformUnifiedToInstagramProfile = (unified: UnifiedInstagramAccount): any => {
    return {
        id: unified.id,
        user_id: unified.user_id,
        instagram_id: unified.instagram_id,
        username: unified.username,
        full_name: unified.full_name,
        biography: unified.biography,
        profile_picture_url: unified.profile_picture_url,
        followers_count: unified.followers_count,
        following_count: unified.following_count,
        posts_count: unified.posts_count,
        is_private: unified.is_private,
        is_verified: unified.is_verified,
        external_url: unified.external_url,
        created_at: unified.created_at,
        updated_at: unified.updated_at,
        last_synced_at: unified.last_synced_at,
        // 추가 데이터
        profile_picture_hd_url: unified.profile_picture_hd_url,
        latest_posts: unified.latest_posts,
        raw_data: {
            fullName: unified.full_name,
            biography: unified.biography,
            profilePicUrl: unified.original_profile_pic_url || unified.profile_picture_url,
            profilePicUrlHD: unified.profile_picture_hd_url,
            followersCount: unified.followers_count,
            followsCount: unified.following_count,
            postsCount: unified.posts_count,
            private: unified.is_private,
            verified: unified.is_verified,
            externalUrl: unified.external_url,
            latestPosts: unified.latest_posts
        }
    };
};

/**
 * UnifiedInstagramAccount를 ConnectedAccount (Dashboard용)로 변환
 */
export const transformUnifiedToConnectedAccount = (unified: UnifiedInstagramAccount): any => {
    return {
        username: unified.username,
        full_name: unified.full_name,
        profile_picture_url: unified.profile_picture_url,
        followers_count: unified.followers_count,
        following_count: unified.following_count,
        posts_count: unified.posts_count,
        is_verified: unified.is_verified,
        biography: unified.biography,
        external_url: unified.external_url,
        latest_posts: unified.latest_posts || [],
        // 추가 정보
        is_private: unified.is_private,
        profile_picture_hd_url: unified.profile_picture_hd_url,
        last_updated: unified.last_synced_at,
        is_imagekit_optimized: unified.is_imagekit_optimized
    };
};

/**
 * 시간 차이를 한국어로 표시하는 유틸리티
 */
export const getTimeAgoKorean = (dateString: string): string => {
    if (!dateString) return '알 수 없음';

    const now = new Date();
    const date = new Date(dateString);
    const diffInMs = now.getTime() - date.getTime();
    const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
    const diffInHours = Math.floor(diffInMinutes / 60);
    const diffInDays = Math.floor(diffInHours / 24);

    if (diffInDays > 0) return `${diffInDays}일 전`;
    if (diffInHours > 0) return `${diffInHours}시간 전`;
    if (diffInMinutes > 0) return `${diffInMinutes}분 전`;
    return '방금 전';
};

/**
 * 숫자를 한국어 단위로 포맷팅 (천, 만, 억)
 */
export const formatNumberKorean = (num: number): string => {
    if (num >= 100000000) {
        return `${(num / 100000000).toFixed(1)}억`;
    }
    if (num >= 10000) {
        return `${(num / 10000).toFixed(1)}만`;
    }
    if (num >= 1000) {
        return `${(num / 1000).toFixed(1)}천`;
    }
    return num.toLocaleString();
};

/**
 * 프로필 이미지 URL 검증 및 fallback 처리
 */
export const getValidProfileImageUrl = (
    imagekitUrl?: string,
    originalUrl?: string,
    hdUrl?: string
): string => {
    // ImageKit 최적화 이미지 우선
    if (imagekitUrl && imagekitUrl.startsWith('http')) {
        return imagekitUrl;
    }

    // HD 이미지 시도
    if (hdUrl && hdUrl.startsWith('http')) {
        return hdUrl;
    }

    // 원본 이미지 시도
    if (originalUrl && originalUrl.startsWith('http')) {
        return originalUrl;
    }

    // 기본 이미지 반환
    return '';
}; 